const BLOCK_TAG_NAMES = new Set(["DIV", "P"]);
const IGNORED_ATTRIBUTE_NAMES = new Set(["aria-hidden", "charset"]);
const LINE_BREAK_TAG_NAMES = new Set(["BR"]);
const PLAIN_TAG_NAMES = new Set(["BR", "DIV", "META", "P", "SPAN"]);
const MARKDOWN_SOURCE_MODE = /"mode":\s*"(markdown|mdx)"/;

const normalizeClipboardText = (text: string) =>
  text
    .replace(/\r\n?/g, "\n")
    .replace(/\u00a0/g, " ")
    .split("\n")
    .map((line) => line.trimEnd())
    .join("\n")
    .trim();

const hasMeaningfulAttributes = (element: Element) =>
  Array.from(element.attributes).some(
    (attribute) => !IGNORED_ATTRIBUTE_NAMES.has(attribute.name) && attribute.value !== ""
  );

const isPlainHtmlElement = (element: Element): boolean =>
  PLAIN_TAG_NAMES.has(element.tagName) && !hasMeaningfulAttributes(element);

const htmlNodeToPlainText = (node: Node): string => {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent ?? "";
  }

  if (!(node instanceof Element)) {
    return "";
  }

  if (LINE_BREAK_TAG_NAMES.has(node.tagName)) {
    return "\n";
  }

  if (node.tagName === "META") {
    return "";
  }

  const text = Array.from(node.childNodes).map(htmlNodeToPlainText).join("");
  return BLOCK_TAG_NAMES.has(node.tagName) ? `${text}\n` : text;
};

const hasOnlyPlainHtmlElements = (root: ParentNode) => Array.from(root.querySelectorAll("*")).every(isPlainHtmlElement);

export const isDartInternalPaste = (clipboardData: DataTransfer | null | undefined) =>
  !!clipboardData?.getData("text/x-dart-editor");

export const clipboardIsMarkdownSource = (clipboardData: DataTransfer | null | undefined): boolean =>
  MARKDOWN_SOURCE_MODE.test(clipboardData?.getData("vscode-editor-data") ?? "");

export const clipboardHtmlIsActuallyPlainText = (html: string, plainText: string) => {
  const dom = new DOMParser().parseFromString(html, "text/html");
  if (!hasOnlyPlainHtmlElements(dom.body)) {
    return false;
  }

  return normalizeClipboardText(htmlNodeToPlainText(dom.body)) === normalizeClipboardText(plainText);
};
